import { useState, useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import {
  BookOpen,
  Search,
  CheckSquare,
  Square,
  Copy,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { getLibraryQuestions, pushQuestionsToTenant } from "../../apiHelper";
import { useAuth } from "../../contexts/AuthContext";
import { Alert, AlertDescription } from "../ui/alert";

const QuestionLibraryImport = ({ accountId }) => {
  const { getAccessTokenSilently } = useAuth();
  const [libraryQuestions, setLibraryQuestions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Get unique categories from questions
  const categories = [
    ...new Set(libraryQuestions.map((q) => q.category).filter(Boolean)),
  ];

  // Filter questions based on category and search
  const filteredQuestions = libraryQuestions.filter((question) => {
    const matchesCategory = !categoryFilter || categoryFilter === "all" || question.category === categoryFilter;
    const matchesSearch =
      !searchKeyword ||
      question.question.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      (question.description &&
        question.description.toLowerCase().includes(searchKeyword.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const loadLibraryQuestions = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);
      
      console.log("Loading library questions...");
      const token = await getAccessTokenSilently();
      
      const response = await getLibraryQuestions(token, {
        page: 1,
        limit: 1000, // Load all questions
        active: true, // Only show active questions
      });

      console.log("Library questions response:", response);

      if (response.success && response.data) {
        // Handle paginated response (result.docs) or direct array
        const questions = Array.isArray(response.data) 
          ? response.data 
          : response.data.docs || [];
        
        console.log("Loaded questions:", questions.length);
        setLibraryQuestions(questions);
        
        if (questions.length === 0) {
          setErrorMessage("No questions found in the library");
        }
      } else {
        console.error("Response not successful:", response);
        setErrorMessage(response.message || "Failed to load questions from library");
      }
    } catch (error) {
      console.error("Error loading library questions:", error);
      setErrorMessage(
        error.response?.data?.message || 
        error.message || 
        "Failed to load questions from library"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedQuestionIds.size === filteredQuestions.length) {
      // Deselect all
      setSelectedQuestionIds(new Set());
    } else {
      // Select all filtered questions
      setSelectedQuestionIds(new Set(filteredQuestions.map((q) => q._id)));
    }
  };

  const handleToggleQuestion = (questionId) => {
    const newSelected = new Set(selectedQuestionIds);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedQuestionIds(newSelected);
  };

  const handleCopySelected = async () => {
    if (selectedQuestionIds.size === 0) {
      setErrorMessage("Please select at least one question to copy");
      return;
    }

    try {
      setCopying(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      const token = await getAccessTokenSilently();
      const selectedQuestionIdsArray = Array.from(selectedQuestionIds);

      const response = await pushQuestionsToTenant(token, accountId, {
        questionIds: selectedQuestionIdsArray,
        active: true, // Always copy as active
      });

      if (response.success) {
        const count = response.data?.pushed || selectedQuestionIdsArray.length;
        setSuccessMessage(
          `Successfully copied ${count} question${count > 1 ? "s" : ""} to tenant`
        );
        setSelectedQuestionIds(new Set());
        // Clear message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setErrorMessage(response.message || "Failed to copy questions");
      }
    } catch (error) {
      console.error("Error copying questions:", error);
      setErrorMessage(
        error.response?.data?.message || "Failed to copy questions"
      );
    } finally {
      setCopying(false);
    }
  };

  const handleCopyAll = async () => {
    if (filteredQuestions.length === 0) {
      setErrorMessage("No questions available to copy");
      return;
    }

    // Select all filtered questions and copy
    setSelectedQuestionIds(new Set(filteredQuestions.map((q) => q._id)));
    // Small delay to update state, then copy
    setTimeout(() => {
      handleCopySelected();
    }, 100);
  };

  const selectedCount = selectedQuestionIds.size;
  const allSelected = filteredQuestions.length > 0 && selectedCount === filteredQuestions.length;

  return (
    <Card className="border-0 shadow-lg shadow-gray-200/50 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="question-import" className="border-0">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <CardHeader className="p-0 flex-1">
              <CardTitle className="flex items-center gap-3 text-lg lg:text-xl text-green-800">
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/25">
                  <BookOpen className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                </div>
                <span>Question Library Import</span>
              </CardTitle>
            </CardHeader>
          </AccordionTrigger>
          <AccordionContent>
            <CardContent className="pt-0">
              {/* Messages */}
              {successMessage && (
                <Alert className="mb-4 bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    {successMessage}
                  </AlertDescription>
                </Alert>
              )}
              {errorMessage && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              {/* Load Questions Button */}
              {libraryQuestions.length === 0 && !loading && (
                <div className="mb-4">
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      loadLibraryQuestions();
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    type="button"
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    Load Questions from Library
                  </Button>
                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-green-600" />
                  <p className="mt-2 text-gray-600">Loading questions...</p>
                </div>
              )}

              {/* Filters and Controls */}
              {libraryQuestions.length > 0 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Category Filter */}
                    <div>
                      <Label htmlFor="category-filter">Filter by Category</Label>
                      <Select
                        value={categoryFilter || "all"}
                        onValueChange={(value) => setCategoryFilter(value === "all" ? "" : value)}
                      >
                        <SelectTrigger id="category-filter">
                          <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          {categories.map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Search */}
                    <div>
                      <Label htmlFor="search">Search Questions</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="search"
                          placeholder="Search questions..."
                          value={searchKeyword}
                          onChange={(e) => setSearchKeyword(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Selection Controls */}
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAll}
                        className="flex items-center gap-2"
                      >
                        {allSelected ? (
                          <>
                            <Square className="h-4 w-4" />
                            Deselect All
                          </>
                        ) : (
                          <>
                            <CheckSquare className="h-4 w-4" />
                            Select All ({filteredQuestions.length})
                          </>
                        )}
                      </Button>
                      {selectedCount > 0 && (
                        <Badge variant="secondary" className="ml-2">
                          {selectedCount} selected
                        </Badge>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyAll}
                        disabled={copying || filteredQuestions.length === 0}
                      >
                        Copy All Visible
                      </Button>
                      <Button
                        onClick={handleCopySelected}
                        disabled={copying || selectedCount === 0}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {copying ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Copying...
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Selected ({selectedCount})
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Questions List */}
                  {loading ? (
                    <div className="text-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-green-600" />
                      <p className="mt-2 text-gray-600">Loading questions...</p>
                    </div>
                  ) : filteredQuestions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No questions found. Try adjusting your filters.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-4 bg-white">
                      {filteredQuestions.map((question) => {
                        const isSelected = selectedQuestionIds.has(question._id);
                        return (
                          <div
                            key={question._id}
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              isSelected
                                ? "bg-green-50 border-green-200"
                                : "bg-white border-gray-200 hover:bg-gray-50"
                            }`}
                            onClick={() => handleToggleQuestion(question._id)}
                          >
                            <div className="mt-1">
                              {isSelected ? (
                                <CheckSquare className="h-5 w-5 text-green-600" />
                              ) : (
                                <Square className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900">
                                {question.question}
                              </p>
                              {question.description && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {question.description}
                                </p>
                              )}
                              <div className="flex gap-2 mt-2 flex-wrap">
                                <Badge variant="outline" className="text-xs">
                                  {question.type}
                                </Badge>
                                {question.category && (
                                  <Badge variant="outline" className="text-xs">
                                    {question.category}
                                  </Badge>
                                )}
                                {question.tags &&
                                  question.tags.length > 0 &&
                                  question.tags.slice(0, 3).map((tag) => (
                                    <Badge
                                      key={tag}
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      {tag}
                                    </Badge>
                                  ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};

export default QuestionLibraryImport;

